'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Play, Sparkles, TrendingUp, Zap, Crown, Clock, AlertCircle, BookOpen } from 'lucide-react';
import HorizontalRail from '@/components/audio-book/HorizontalRail';
import { extractAgeRange } from '@/lib/recommendation-engine';

interface AudioBook {
  id: string;
  title: string;
  prompt: string;
  ageBucket: string | null;
  pageCount: number;
  coverImageUrl: string | null;
  totalDuration: number | null;
  montessoriMode: boolean;
  montessoriFocus: string | null;
  isFeatured: boolean;
  isFeaturedSample?: boolean;
  createdAt: string;
  language: string;
  hashtags: string;
  progress?: number;
  bilingualMode?: boolean;
  isLearningContent?: boolean;
  sourceLanguage?: string;
  targetLanguage?: string;
}

export default function AudioBookLibraryWithRecs() {
  const { data: session } = useSession();
  const router = useRouter();
  
  // Rails state
  const [continueListening, setContinueListening] = useState<AudioBook[]>([]);
  const [forYouBooks, setForYouBooks] = useState<AudioBook[]>([]);
  const [becauseBooks, setBecauseBooks] = useState<AudioBook[]>([]);
  const [mostlyLikeBooks, setMostlyLikeBooks] = useState<AudioBook[]>([]);
  const [trendingBooks, setTrendingBooks] = useState<AudioBook[]>([]);
  const [freeFeatured, setFreeFeatured] = useState<AudioBook[]>([]);
  const [myBooks, setMyBooks] = useState<AudioBook[]>([]);
  const [allPublicBooks, setAllPublicBooks] = useState<AudioBook[]>([]); // ALL public books for authenticated users
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [userFavorites, setUserFavorites] = useState<Set<string>>(new Set());
  const [userWatchlist, setUserWatchlist] = useState<Set<string>>(new Set());
  const [seedBookTitle, setSeedBookTitle] = useState<string>('');
  const [showAgeBanner, setShowAgeBanner] = useState(false);
  const [heroBook, setHeroBook] = useState<AudioBook | null>(null);
  const [heroIndex, setHeroIndex] = useState(0);

  // Initial load
  useEffect(() => {
    if (session?.user) {
      // Authenticated user: load personalized rails
      fetchAuthenticatedRails();
    } else {
      // Non-authenticated: load free featured + popular
      fetchNonAuthenticatedRails();
    }
  }, [session]);

  // Auto-rotate hero book every 7 seconds
  useEffect(() => {
    const heroPool = session?.user
      ? [...trendingBooks, ...forYouBooks].filter(b => b.coverImageUrl)
      : [...freeFeatured, ...forYouBooks].filter(b => b.coverImageUrl);
    
    if (heroPool.length === 0) return;
    
      const interval = setInterval(() => {
      setHeroIndex(prev => {
        const next = (prev + 1) % heroPool.length;
        setHeroBook(heroPool[next]);
        return next;
      });
    }, 7000);
    
      return () => clearInterval(interval);
  }, [trendingBooks, forYouBooks, freeFeatured, session?.user]);

  async function fetchAuthenticatedRails() {
    setLoading(true);
    
    try {
      // Fetch ALL rails in parallel for consistent loading (including ALL public books!)
      const [continueRes, forYouRes, favoritesRes, watchlistRes, myBooksRes, becauseRes, mostlyRes, trendingBooks, publicBooksRes] = await Promise.all([
        fetch('/api/recs/continue?limit=12'),
        fetch('/api/recs/for-you?limit=24'),
        fetch('/api/favorites'),
        fetch('/api/watchlist'),
        fetch('/api/audio-book/my-books'),
        fetch('/api/recs/because?limit=12'),
        fetch('/api/recs/mostly-like?limit=24'),
        fetchTrendingForUser(),
        fetch('/api/audio-book/public'), // ✅ CRITICAL FIX: Fetch ALL public books for authenticated users too!
      ]);

      // Process all responses
      if (continueRes.ok) {
        const data = await continueRes.json();
        setContinueListening(data.books || []);
      }

      if (forYouRes.ok) {
        const data = await forYouRes.json();
        setForYouBooks(data.books || []);
      }

      if (favoritesRes.ok) {
        const favData = await favoritesRes.json();
        setUserFavorites(new Set(favData.favorites.map((f: any) => f.bookId)));
      }

      if (watchlistRes.ok) {
        const watchData = await watchlistRes.json();
        setUserWatchlist(new Set(watchData.watchlist.map((w: any) => w.bookId)));
      }

      if (myBooksRes.ok) {
        const myData = await myBooksRes.json();
        setMyBooks(myData.audioBooks || []);
      }

      if (becauseRes.ok) {
        const data = await becauseRes.json();
        setBecauseBooks(data.books || []);
        setSeedBookTitle(data.seedBook?.title || '');
      }

      if (mostlyRes.ok) {
        const data = await mostlyRes.json();
        setMostlyLikeBooks(data.books || []);
      }

      if (trendingBooks && trendingBooks.length > 0) {
        setTrendingBooks(trendingBooks);
        // Set hero book from trending for authenticated users
        if (!heroBook) {
          setHeroBook(trendingBooks[0]);
        }
      }

      // ✅ CRITICAL FIX: Process ALL public books for authenticated users
      if (publicBooksRes.ok) {
        const data = await publicBooksRes.json();
        const allBooks = data.audioBooks || [];
        
        // Show all public books sorted by featured status + date
        const sortedPublic = allBooks
          .sort((a: AudioBook, b: AudioBook) => {
            // Featured first
            if (a.isFeatured !== b.isFeatured) {
              return a.isFeatured ? -1 : 1;
            }
            // Then by creation date
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
        
        setAllPublicBooks(sortedPublic);
        console.log('[Library] Loaded', sortedPublic.length, 'public books for authenticated user');
      }
    } catch (error) {
      console.error('Error fetching authenticated rails:', error);
    } finally {
      setLoading(false);
    }
  }


  async function fetchTrendingForUser(): Promise<AudioBook[]> {
    try {
      // Get user's age range
      const accountRes = await fetch('/api/account');
      if (accountRes.ok) {
        const account = await accountRes.json();
        const ageRange = extractAgeRange(account.kidAgeRange || null);
        
        // Check if age is set
        if (!account.kidAgeRange) {
          setShowAgeBanner(true);
        }
        
        const trendingRes = await fetch(`/api/recs/trending?ageMin=${ageRange.min}&ageMax=${ageRange.max}&limit=12`);
        if (trendingRes.ok) {
          const data = await trendingRes.json();
          return data.books || [];
        }
      }
      return [];
    } catch (error) {
      console.error('Error fetching trending books:', error);
      return [];
    }
  }

  async function fetchNonAuthenticatedRails() {
    setLoading(true);
    
    try {
      const [samplesRes, publicRes] = await Promise.all([
        fetch('/api/audio-book/samples'),
        fetch('/api/audio-book/public'),
      ]);

      if (samplesRes.ok) {
        const data = await samplesRes.json();
        const samples = data.audioBooks || [];
        setFreeFeatured(samples);
        // Set hero book from featured samples
        if (samples.length > 0 && !heroBook) {
          setHeroBook(samples[0]);
        }
      }

      if (publicRes.ok) {
        const data = await publicRes.json();
        const allBooks = data.audioBooks || [];
        
        // Show most popular (based on isFeatured, createdAt)
        const popular = allBooks
          .sort((a: AudioBook, b: AudioBook) => {
            // Featured first
            if (a.isFeatured !== b.isFeatured) {
              return a.isFeatured ? -1 : 1;
            }
            // Then by creation date
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          })
          .slice(0, 24);
        
        setForYouBooks(popular);
        // Set hero book from popular if no featured samples
        if (popular.length > 0 && !heroBook) {
          setHeroBook(popular[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching non-authenticated rails:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleBookClick(book: AudioBook, rail: string, slotIndex: number) {
    // Track impression with rail + slot
    if (session?.user) {
      try {
        await fetch('/api/watch-history', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookId: book.id,
            impressionRail: rail,
            slotIndex,
            progress: 0,
          }),
        });
      } catch (error) {
        console.error('Error tracking click:', error);
      }
    }
    
    // Navigate to book
    router.push(`/audio-book/${book.id}`);
  }

  function handleRailImpression(rail: string, books: AudioBook[]) {
    // Optional: log impression to analytics
    console.log(`Rail "${rail}" impression with ${books.length} books`);
  }

  async function handleFavoriteToggle(bookId: string, isAdding: boolean) {
    try {
      if (isAdding) {
        const res = await fetch('/api/favorites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bookId }),
        });
        if (!res.ok) throw new Error('Failed to add to favorites');
      } else {
        const res = await fetch(`/api/favorites?bookId=${bookId}`, {
          method: 'DELETE',
        });
        if (!res.ok) throw new Error('Failed to remove from favorites');
      }
      
      setUserFavorites(prev => {
        const next = new Set(prev);
        isAdding ? next.add(bookId) : next.delete(bookId);
        return next;
      });
    } catch (error) {
      console.error('Favorite toggle error:', error);
      throw error;
    }
  }

  async function handleWatchlistToggle(bookId: string, isAdding: boolean) {
    try {
      if (isAdding) {
        const res = await fetch('/api/watchlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bookId }),
        });
        if (!res.ok) throw new Error('Failed to add to watchlist');
      } else {
        const res = await fetch(`/api/watchlist?bookId=${bookId}`, {
          method: 'DELETE',
        });
        if (!res.ok) throw new Error('Failed to remove from watchlist');
      }
      
      setUserWatchlist(prev => {
        const next = new Set(prev);
        isAdding ? next.add(bookId) : next.delete(bookId);
        return next;
      });
    } catch (error) {
      console.error('Watchlist toggle error:', error);
      throw error;
    }
  }

  return (
    <div
      style={{
      minHeight: '100vh',
        background: 'linear-gradient(180deg, #1a0b2e 0%, #0f0520 100%)',
      paddingBottom: '4rem',
      }}
    >
      {/* Netflix-Style Hero Banner */}
      {heroBook && (
        <div
          style={{
          position: 'relative',
            width: '100%',
            height: 'clamp(500px, 70vh, 800px)',
            marginBottom: '2rem',
            overflow: 'hidden',
          }}
        >
          {/* Background Image */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: heroBook.coverImageUrl
                ? `url(${heroBook.coverImageUrl})`
                : 'linear-gradient(135deg, #667eea, #764ba2)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.5)',
              transition: 'all 1s ease-in-out',
            }}
          />
          
          {/* Gradient Overlay (Netflix-style vignette) */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(to bottom, rgba(15, 5, 32, 0.3) 0%, rgba(15, 5, 32, 0.8) 70%, rgba(15, 5, 32, 1) 100%), linear-gradient(to right, rgba(15, 5, 32, 0.8) 0%, transparent 50%)',
            }}
          />
          
          {/* Content Overlay */}
          <div
            style={{
              position: 'absolute',
              bottom: '10%',
              left: '5%',
              maxWidth: '600px',
              zIndex: 2,
              padding: '0 2rem',
            }}
          >
            {/* Trending Badge */}
            <div
              style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
                background: 'rgba(220, 38, 38, 0.9)',
                borderRadius: '8px',
              marginBottom: '1rem',
                fontWeight: 700,
                fontSize: '0.875rem',
                color: 'white',
              }}
            >
              <TrendingUp size={18} />
              TRENDING NOW
            </div>
            
            {/* Title */}
            <h1
              style={{
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              fontWeight: 900,
              color: 'white',
              margin: '0 0 1rem 0',
                textShadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
                lineHeight: 1.2,
              }}
            >
              {heroBook.title}
            </h1>
            
            {/* Description */}
            <p
              style={{
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
              color: 'rgba(255, 255, 255, 0.95)',
                marginBottom: '1.5rem',
              lineHeight: 1.6,
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.8)',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {heroBook.prompt.split(/Please create|Continue the story/i)[0].trim().substring(0, 200)}
              {heroBook.prompt.length > 200 ? '...' : ''}
            </p>
            
            {/* CTA Buttons */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button
                onClick={() => handleBookClick(heroBook, 'hero', 0)}
              style={{
                  display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem 2.5rem',
                background: 'white',
                  color: '#0f0520',
                border: 'none',
                  borderRadius: '8px',
                fontSize: '1.125rem',
                fontWeight: 700,
                cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              }}
              onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
              }}
              onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = 'white';
              }}
            >
                <Play size={24} fill="currentColor" />
                Play Now
            </button>
              
              {heroBook.pageCount && (
                <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                    padding: '1rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                    borderRadius: '8px',
                color: 'white',
                    fontSize: '0.9375rem',
                fontWeight: 600,
                  }}
                >
                  <BookOpen size={20} />
                  {heroBook.pageCount} pages
                </div>
            )}
          </div>
          </div>
        </div>
      )}
      
      {/* Simple Header (if no hero book yet) */}
      {!heroBook && (
        <div
                  style={{
            textAlign: 'center',
            marginBottom: '3rem',
            paddingTop: '8rem',
            paddingLeft: '2rem',
            paddingRight: '2rem',
          }}
        >
          <h1
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: 900,
              margin: '0 0 1rem 0',
              background: 'linear-gradient(135deg, #ffffff 0%, #a78bfa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Audio Picture Book Library
          </h1>
          <p
            style={{
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                      color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            {session?.user
              ? 'Personalized stories just for you'
              : 'Discover magical audio adventures for kids'}
          </p>
          </div>
        )}

      {/* Age Banner (if age not set) */}
      {showAgeBanner && session?.user && (
        <div
          style={{
            margin: '0 2rem 2rem 2rem',
            padding: '1rem 1.5rem',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
          }}
        >
          <AlertCircle size={24} color="#a78bfa" />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, color: 'white', fontWeight: 600 }}>
              Set your child's age for better recommendations
            </p>
            <p style={{ margin: '0.25rem 0 0 0', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
              We'll suggest age-appropriate stories tailored to your child
            </p>
          </div>
          <button
            onClick={() => router.push('/account')}
                      style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 600,
                        cursor: 'pointer',
            }}
          >
            Set Age
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div
          style={{
                          display: 'flex',
                          justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '4px solid rgba(139, 92, 246, 0.2)',
              borderTop: '4px solid #8b5cf6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
              </div>
            )}

      {/* Rails Content */}
      {!loading && (
        <div style={{ paddingBottom: '2rem' }}>
          {/* AUTHENTICATED USER RAILS */}
          {session?.user && (
            <>
              {/* My Generated Books */}
              {myBooks.length > 0 && (
                <HorizontalRail
                  title="My Generated Books"
                  icon={<Crown size={24} />}
                  books={myBooks}
                  rail="my_books"
                onBookClick={handleBookClick}
                  onImpression={handleRailImpression}
                  userFavorites={userFavorites}
                  userWatchlist={userWatchlist}
                  onFavoriteToggle={handleFavoriteToggle}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              )}

              {/* Continue Listening */}
              {continueListening.length > 0 && (
                <HorizontalRail
                  title="Continue Listening"
                  icon={<Play size={24} />}
                  books={continueListening}
                  rail="continue"
                  onBookClick={handleBookClick}
                  onImpression={handleRailImpression}
                  userFavorites={userFavorites}
                  userWatchlist={userWatchlist}
                  onFavoriteToggle={handleFavoriteToggle}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              )}

              {/* For You */}
              {forYouBooks.length > 0 && (
                <HorizontalRail
                  title="For You"
                  icon={<Sparkles size={24} />}
                  books={forYouBooks}
                  rail="for_you"
                    onBookClick={handleBookClick}
                  onImpression={handleRailImpression}
                  userFavorites={userFavorites}
                  userWatchlist={userWatchlist}
                  onFavoriteToggle={handleFavoriteToggle}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              )}

              {/* Because You Watched */}
              {becauseBooks.length > 0 && (
                <HorizontalRail
                  title={`Because You Watched${seedBookTitle ? ` "${seedBookTitle}"` : ''}`}
                  icon={<Zap size={24} />}
                  books={becauseBooks}
                  rail="because_watched"
                onBookClick={handleBookClick}
                  onImpression={handleRailImpression}
                  userFavorites={userFavorites}
                  userWatchlist={userWatchlist}
                  onFavoriteToggle={handleFavoriteToggle}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              )}

              {/* Mostly You'd Like */}
              {mostlyLikeBooks.length > 0 && (
                <HorizontalRail
                  title="Mostly You'd Like"
                  icon={<Sparkles size={24} />}
                  books={mostlyLikeBooks}
                  rail="mostly_like"
                  onBookClick={handleBookClick}
                  onImpression={handleRailImpression}
                  userFavorites={userFavorites}
                  userWatchlist={userWatchlist}
                  onFavoriteToggle={handleFavoriteToggle}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              )}

              {/* Trending */}
              {trendingBooks.length > 0 && (
                <HorizontalRail
                  title="Trending for Your Age"
                  icon={<TrendingUp size={24} />}
                  books={trendingBooks}
                  rail="trending"
                  onBookClick={handleBookClick}
                  onImpression={handleRailImpression}
                  userFavorites={userFavorites}
                  userWatchlist={userWatchlist}
                  onFavoriteToggle={handleFavoriteToggle}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              )}

              {/* ✅ CRITICAL FIX: ALL Public Books Rail - Always visible for authenticated users */}
              {allPublicBooks.length > 0 && (
                <HorizontalRail
                  title="All Stories"
                  subtitle="Browse our entire collection"
                  icon={<BookOpen size={24} />}
                  books={allPublicBooks}
                  rail="all_public"
                  onBookClick={handleBookClick}
                  onImpression={handleRailImpression}
                  userFavorites={userFavorites}
                  userWatchlist={userWatchlist}
                  onFavoriteToggle={handleFavoriteToggle}
                  onWatchlistToggle={handleWatchlistToggle}
                />
              )}
          </>
        )}

          {/* NON-AUTHENTICATED USER RAILS */}
          {!session?.user && (
            <>
              {/* Free Featured */}
              {freeFeatured.length > 0 && (
                <HorizontalRail
                  title="Free Featured Stories"
                  icon={<Sparkles size={24} />}
                  books={freeFeatured}
                  rail="free_featured"
                  onBookClick={handleBookClick}
                />
              )}

              {/* Popular Now */}
              {forYouBooks.length > 0 && (
                <HorizontalRail
                  title="Popular Now"
                  icon={<TrendingUp size={24} />}
                  books={forYouBooks}
                  rail="popular_now"
                  onBookClick={handleBookClick}
                />
              )}

              {/* Sign-in Prompt */}
              <div
                style={{
                  margin: '4rem 2rem',
            padding: '3rem 2rem',
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '24px',
            textAlign: 'center',
                }}
              >
                <Sparkles size={48} color="#a78bfa" style={{ margin: '0 auto 1rem auto' }} />
                <h3
                  style={{
              fontSize: '1.75rem',
              fontWeight: 800,
                color: 'white',
                    marginBottom: '0.5rem',
                  }}
                >
                  Unlock Personalized Recommendations
                </h3>
                <p
                  style={{
                    fontSize: '1rem',
                    color: 'rgba(255, 255, 255, 0.8)',
                    marginBottom: '2rem',
            maxWidth: '500px',
                    margin: '0 auto 2rem auto',
                  }}
                >
                  Sign in to get stories tailored just for your child, track progress, and access exclusive content
                </p>
                <button
                  onClick={() => router.push('/login')}
              style={{
                padding: '1rem 2rem',
                    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                fontSize: '1.125rem',
                fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.4)',
                  }}
                >
                  Sign In
                </button>
          </div>
            </>
          )}
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

